import type { Mentor, MentorCategory } from "../types/hackathon";

type MentorSeed = Omit<Mentor, "linkedin" | "photo" | "groupIds" | "inviteToken" | "joinedAt" | "category"> & {
  category: MentorCategory;
};

const seed = (m: MentorSeed): Mentor => ({
  linkedin: "",
  photo: "",
  groupIds: [],
  inviteToken: `mentor-${m.id}`,
  joinedAt: "2026-10-01T00:00:00.000Z",
  ...m,
});

// Source: "Meet Your Mentors & Judges" deck, The Hardware Hackathon 2nd Edition.
export const mentors: Mentor[] = [
  // Judges
  seed({
    id: "tess-hau",
    category: "judge",
    name: "Tess Hau",
    title: "Founder, Tess Ventures & Gaia Labs",
    organization: "Tess Ventures & Gaia Labs",
    expertise: "Demo Day judge",
    email: "Tess@tesshau.com",
    photo: "https://cdn.magicpatterns.com/uploads/gxVmWTBebe9pt8dkZbFzF7/image.png",
    photoPosition: "50% 25%",
    linkedin: "https://www.linkedin.com/in/tesshau/",
    bio: "Tess Hau is a Palo Alto–based venture capitalist and the founder, CEO and General Partner of Tess Ventures, which she has led since 2017. She invests in frontier tech, including robotics, artificial intelligence, Web3 and gaming, and also works as an LP, advisor and mentor to founders. A global speaker, she regularly judges pitch competitions and hosts founder events such as the Global AI Pitch Summit in Silicon Valley. She studied at York University and Stanford.",
    askAbout: ["Venture capital & fundraising", "Robotics & AI investing", "Pitching to investors"],
    availability: "Demo Day judge: Tuesday Oct 13, from 2 PM PT, on site at Circuit Launch.",
  }),
  seed({
    id: "tess-robinson",
    category: "judge",
    name: "Tess Robinson",
    title: "Managing Partner, FTB Capital Group · Founder, HER Connections",
    organization: "HER Connections",
    expertise: "Demo Day judge",
    email: "tess@herconnections.ai",
    photo: "https://cdn.magicpatterns.com/uploads/354kCztgPLKiX1aBJB2cwG/image.png",
    photoPosition: "47% 20%",
    linkedin: "https://www.linkedin.com/in/tess-robinson/",
    bio: "Tess Robinson is a Palo Alto–based investor and operator. As Managing Partner of FTB Capital Group, she backs founders at the intersection of health, longevity and AI, pairing capital with hands-on operating support. Through HER Connections she works as a fractional executive for startups, building marketing, sales, operations and fundraising systems, and she founded Pacific Standard Co., a performance accelerator that helps founders scale without burning out. She previously co-founded Slay Ventures and spent 15+ years as a critical care provider.",
    askAbout: ["Go-to-market & sales systems", "Fundraising readiness & pitch decks", "Health, longevity & wearables"],
    availability: "Demo Day judge: Tuesday Oct 13, from 2 PM PT, on site at Circuit Launch.",
  }),
  seed({
    id: "clodoaldo-araujo",
    category: "judge",
    name: "Clodoaldo Araujo",
    title: "Entrepreneur · Host, Rota da Inovação",
    organization: "Rota da Inovação · CA3 Steel",
    expertise: "Demo Day judge",
    email: "contato@clodoaldoaraujo.com.br",
    photo: "https://cdn.magicpatterns.com/uploads/cy6TJpy52qmzy7HgRV8Upx/image.png",
    photoPosition: "50% 35%",
    linkedin: "https://www.linkedin.com/in/clodoaldoaraujo/",
    bio: "Clodoaldo Araujo is a Brazilian entrepreneur, speaker and TV host based in Limeira, São Paulo. A mechanical and civil engineer with an MBA in Entrepreneurship from California State University, Fresno, he won O Aprendiz 5 – O Sócio in 2008, earning a partnership with businessman Roberto Justus. He runs companies in steel and construction, including CA3 Steel and the iSteel app, is CEO of Spyder Race, hosts Rota da Inovação, a show that has explored innovation in more than 60 countries, and facilitates Sebrae's Empretec entrepreneurship program.",
    askAbout: ["Entrepreneurship & business strategy", "Innovation around the world", "Pitching & public speaking"],
    availability: "Demo Day judge: Tuesday Oct 13, from 2 PM PT, on site at Circuit Launch.",
  }),

  // Mentors
  seed({
    id: "matt-tom",
    category: "mentor",
    name: "Matt Tom",
    title: "Founder & President, MTCC LLC",
    organization: "MTCC LLC",
    expertise: "Commercialization & practical design",
    email: "matt@mtcc.io",
    photo: "https://cdn.magicpatterns.com/uploads/pJuCGKGgusY8iKp1FWngvH/image.png",
    note: "Misses the Oct 7 kickoff — intro video shared",
    bio: "Matt Tom runs MTCC, a Bay Area boutique consultancy specializing in design for manufacturing, product-market fit, commercialization and manufacturing scale-up, mainly in Food & Beverage. He is best known for commercializing and scaling iconic plant-based products such as Kite Hill, Impossible Foods, EAT JUST and Ripple Foods into nationwide retail. He started in biomedical-device R&D and spent nearly a decade at USDA-ARS.",
    askAbout: ["Design for manufacturing", "Commercialization & scale-up", "Product-market fit"],
    availability:
      "Thu Oct 8 – Sun Oct 11, lunchtime or after; Sat–Sun most any time (not Mon Oct 12). Zoom or in person at Circuit Launch. Book on Zoho.",
  }),
  seed({
    id: "raza-rafiq",
    category: "mentor",
    name: "Raza Rafiq",
    title: "Founder, Goose Industries",
    organization: "Goose Industries",
    expertise: "Commercialization",
    email: "raza@goose-industries.com",
    location: "Circuit Launch Mountain View",
    photo: "https://cdn.magicpatterns.com/uploads/gvWfAo7bYrQQ5iVJ7apniQ/image.png",
    bio: "Raza Rafiq founded Goose Industries, an AI company building autonomous operating systems for the physical world by combining AI reasoning, computer vision, robotics and real-world operational data. He previously co-founded Altree Developments, a tech-enabled real estate platform spanning ~3,000 homes and over $2.5B in assets, and worked at Mighty Buildings, a robotics and 3D-printing home builder.",
    askAbout: ["Product & go-to-market", "Fundraising", "Operations"],
    availability: "Book any open slot via Google Calendar. Virtual by default. At kickoff Oct 7 and Demo Day Oct 13.",
  }),
  seed({
    id: "dominick-lee",
    category: "mentor",
    name: "Dominick Lee",
    title: "Founder & CEO, GyroPalm",
    organization: "GyroPalm",
    expertise: "Edge AI, dual-use hardware & HMI",
    email: "dlee@gyropalm.com",
    location: "Circuit Launch Oakland",
    photo: "https://cdn.magicpatterns.com/uploads/cyiLobW1N9uXnGM3TquhSj/image.png",
    bio: "Dominick Lee is founder and CEO of GyroPalm, an award-winning robotics and wearable AI company building intuitive human-machine interfaces for industrial automation, robotics and spatial computing. An inventor, programmer and systems architect, he has built patented wearables that let people control robots, machines and connected devices through gestures. He is Roboticist in Residence at Circuit Launch.",
    askAbout: ["Wearables & human-machine interfaces", "Robotics & industrial automation", "Edge computing / AI"],
    availability:
      "Fri Oct 9, 2–8 PM · Sat Oct 10, 4–8 PM · Mon Oct 12, 2–8 PM. In person at his Circuit Launch office or virtual.",
    bookingUrl: "https://calendly.com/gyropalm/60min",
  }),
  seed({
    id: "thomas-waters",
    category: "mentor",
    name: "Thomas Waters",
    title: "Mechanical Engineering Consultant",
    organization: "Independent consultant",
    expertise: "CAD, fabrication & DFM",
    email: "tww0014@gmail.com",
    location: "Circuit Launch Oakland",
    photo: "https://cdn.magicpatterns.com/uploads/nZUHg139b79qFkPkf638P2/image.png",
    note: "Joins from Oct 8",
    bio: "Thomas Waters is a mechanical engineering consultant with 10+ years of experience in design, design for manufacture (DFM), and small-scale prototyping and fabrication. He is hands-on with CAD, 3D printing, laser cutting and light metal machining (less so electronics and PCB design). Ask him how to design a part in CAD and make it quickly with the tools available at Circuit Launch.",
    askAbout: ["CAD design", "3D printing & laser cutting", "Design for manufacture"],
    availability: "Fri Oct 9, 10 AM–2 PM, in person at Circuit Launch (to be confirmed). Otherwise email or Slack him.",
  }),
  seed({
    id: "dhruv-diddi",
    category: "mentor",
    name: "Dhruv Diddi",
    title: "Principal, Physical AI Ecosystem, Nebius · Founder, Solo Tech",
    organization: "Nebius · Solo Tech",
    expertise: "Physical AI infrastructure",
    email: "dhruv.diddi@nebius.com",
    location: "Circuit Launch Mountain View",
    photo: "https://cdn.magicpatterns.com/uploads/gT96aQdBSdpg2XSnxZTFrM/image.png",
    bio: "Dhruv Diddi leads the Physical AI Ecosystem at Nebius, working across infrastructure, ecosystem growth and engineering, with a focus on production-grade inference and high-throughput training. He also founded Solo Tech, an open-source Physical AI platform that opens up access to robots through education: a 4,500+ builder community with Physical AI Hubs in 20+ locations, backed by the Nebius Fellow Program, NVIDIA Inception and a Meta Impact Grant.",
    askAbout: ["Physical AI infrastructure", "Inference & training at scale", "Open-source robotics ecosystem"],
    availability: "Book any open slot via his Calendly.",
    bookingUrl: "https://calendly.com/dhruvdiddi/sync",
  }),
  seed({
    id: "ark-baltser",
    category: "mentor",
    name: "Ark Baltser",
    title: "Co-founder & CEO, Petpin AI",
    organization: "Petpin AI",
    expertise: "AI wearables & hardware startups",
    email: "ark@petpin.ai",
    location: "Circuit Launch Mountain View",
    photo: "https://cdn.magicpatterns.com/uploads/xea282dcFA6abhpTxeBGW1/image.png",
    photoPosition: "40% 20%",
    linkedin: "https://www.linkedin.com/in/arkadiybaltser/",
    bio: "Ark (Arkadiy) Baltser is co-founder and CEO of Petpin AI, a Los Altos–based startup building an AI wearable for animals. Its collar captures live point-of-view video, GPS and behaviour insights, and is now in beta on pets, working horses, cattle and K9 search-and-rescue teams, including the Mountain View Fire Department. He previously founded Plutoview, a multi-screen collaboration tool for remote teams that was #1 Product of the Week on Product Hunt. A speaker and community builder, he has hosted 120+ events and studied the history and philosophy of science and technology at Boston University.",
    askAbout: ["Building a wearable from prototype to beta", "Field testing with real users", "Early-stage fundraising & community"],
    availability: "In Mountain View all week, at kickoff Oct 7.",
  }),
  seed({
    id: "nick-velasquez",
    category: "mentor",
    name: "Nick Velasquez",
    title: "CEO & Co-founder, DeepSea Developments",
    organization: "DeepSea Developments",
    expertise: "Electronics, prototype to production",
    linkedin: "https://www.linkedin.com/in/electronick/",
    email: "nick@dsd.dev",
    location: "Circuit Launch Mountain View",
    photo: "https://cdn.magicpatterns.com/uploads/pMWndVQja66awPYvZr77Kn/image.png",
    bio: "Nick Velasquez is CEO and co-founder of DeepSea Developments, a Palo Alto electronics product-development company that takes hardware from prototype to production. His team has helped bring more than 10,000 IoT devices to market, working on consumer electronics, wearables, medical devices and industrial automation, and DeepSea is a Raspberry Pi design partner, Arduino Gold Pro partner and Seeed Studio Ranger. He also co-founded MubOn, which builds fast EV-charging stations in Colombia. An electrical and electronics engineer from Universidad de los Andes with a master's from Pontificia Universidad Javeriana, he started out designing PCBs, embedded Linux systems and firmware.",
    askAbout: ["PCB design & electronics", "Raspberry Pi, Arduino & IoT prototyping", "Designing for manufacturing"],
    availability: "In SF for Tech Week Oct 7–9.",
  }),
  seed({
    id: "ralph-richart",
    category: "mentor",
    name: "Ralph Richart",
    title: "Founder, MetisLogic / Partsnap",
    organization: "MetisLogic / Partsnap",
    expertise: "PCB manufacturing & supply chain",
    linkedin: "https://www.linkedin.com/in/ralphrichart/",
    email: "ralph@partsnap.io",
    photo: "https://cdn.magicpatterns.com/uploads/p9u9KKJH8CjvYYSjuUgbZH/image.png",
    note: "Away Oct 8–11, back for Demo Day",
    bio: "Ralph Richart is an electronics-manufacturing executive with more than 30 years of experience in supply chain, logistics, manufacturing and technology development. He founded MetisLogic, which builds software to streamline manufacturing readiness for electronics manufacturers and their customers, and consults on operations for the wireless-power company Reach. He was previously CTO and Head of Manufacturing at Tempo Automation, a software-driven circuit-board assembly company serving aerospace, medtech and semiconductor customers. Earlier he ran manufacturing at Advanced Circuits and spent 23 years as President of Coastal Circuits, a quick-turn PCB maker in Redwood City.",
    askAbout: ["PCB manufacturing & assembly", "Getting ready for production", "Supply chain & sourcing parts"],
    availability: "Away Oct 8–11, back for Demo Day.",
  }),
  seed({
    id: "nelio-batista",
    category: "mentor",
    name: "Nelio Batista",
    title: "Co-founder & CTO, ElectricFish",
    organization: "ElectricFish",
    expertise: "EV charging & high-power systems",
    email: "nelio@electricfish.co",
    photo: "https://cdn.magicpatterns.com/uploads/fQaCQJMP4B9Fzuw8AGFhb5/image.png",
    photoPosition: "50% 45%",
    linkedin: "https://www.linkedin.com/in/neliobatistanjr/",
    bio: "Nelio Batista is co-founder and CTO of ElectricFish, a Silicon Valley company building battery-integrated EV fast chargers for gas stations and other sites with limited grid power. He is a multidisciplinary engineer with a decade of experience in software architecture and high-power systems. His background is in aerospace research, and at ElectricFish he leads the technology that manages when energy flows in and out, so fast charging supports the grid instead of straining it.",
    askAbout: ["Hardware & energy systems", "Software architecture", "Building a deep-tech startup"],
  }),

  // Office hours
  seed({
    id: "kash-kompella",
    category: "office-hours",
    name: "Kashyap (Kash) Kompella",
    title: "Founder & President, TPH Ventures",
    organization: "TPH Ventures",
    expertise: "Healthcare AI & go-to-market",
    email: "kashyap@tphventures.com",
    photo: "https://cdn.magicpatterns.com/uploads/dT4kpvJtZ6c2c4d2gWWaZm/image.png",
    bio: "Kash Kompella is an engineer, entrepreneur and community builder. He founded TPH Ventures, a healthcare AI venture studio helping regulated AI and MedTech companies move from pilot to clinical and operational scale. With experience across education and healthcare, he brings a hands-on view of product development, customer discovery, regulatory strategy, fundraising and go-to-market.",
    askAbout: ["Customer discovery & go-to-market", "Healthcare AI / MedTech regulation", "Fundraising"],
    availability: "Book via Calendly; happy to drop in at Circuit Launch during the week. Masterclass on Oct 8.",
    bookingUrl: "https://calendly.com/kashyapk",
  }),
  seed({
    id: "aki-machida",
    category: "office-hours",
    name: "Aki Machida",
    title: "President, Global Innovator Solutions (Gloinns)",
    organization: "Gloinns",
    expertise: "U.S.–Japan go-to-market",
    email: "aki.machida@gloinns.com",
    photo: "https://cdn.magicpatterns.com/uploads/5LCjay39BpxLyNyLdFqGn2/image.png",
    bio: "Aki Machida helps Japanese companies succeed in the U.S. market and supports U.S. and European startups expanding into Japan. Drawing on 20+ years in Silicon Valley and a 40-year career in semiconductors, sensors, displays and advanced manufacturing, he provides hands-on go-to-market and cross-border commercialization support. He has led multiple >$1B product lines.",
    askAbout: ["U.S.–Japan market entry", "Business development & GTM", "Investor readiness & pitch"],
    availability: "On site at Circuit Launch from Wed Oct 7 through lunch Mon Oct 12 — find him in person.",
  }),
  seed({
    id: "eva-xiao",
    category: "office-hours",
    name: "Eva Xiao",
    title: "Co-founder & CEO, AXIS Connect",
    organization: "AXIS Connect",
    expertise: "U.S.–Asia commercialization",
    email: "eva@axisconnect.ai",
    photo: "https://cdn.magicpatterns.com/uploads/2D77oyFZr5jLCvF7t5ZVch/image.png",
    bio: "Eva Xiao is co-founder and CEO of AXIS Connect and leads Asia business partnerships and ecosystem development at Circuit Launch. She previously worked at Alibaba, Huawei/Honor and Tencent, and now helps robotics and Physical AI companies bridge technology, manufacturing, commercialization and market opportunities between the U.S. and Asia.",
    askAbout: ["U.S.–Asia ecosystem & supply chain", "Commercialization & U.S. market entry", "Partnerships"],
    availability:
      "Wed Oct 7 AM (kickoff) · Thu Oct 8 working lunch · Tue Oct 13, 11 AM–2 PM (Demo Day) · drop-ins all week. In person at Circuit Launch.",
  }),
  seed({
    id: "jose-garcia",
    category: "office-hours",
    name: "Jose Garcia",
    title: "Distribution Channel Manager, Bay Area, Keysight",
    organization: "Keysight",
    expertise: "Market development & sales",
    email: "jose.garcia@keysight.com",
    photo: "https://cdn.magicpatterns.com/uploads/smEQE6uCsEwx41WFRHLdP3/image.png",
    bio: "Jose Garcia manages distribution partner sales for test and measurement equipment at Keysight. He has 25+ years identifying new business opportunities in Fortune 500 companies and startups, and 10+ years supporting accelerators with startup sourcing, training and deal sourcing. He has led 50+ multi-million-dollar product and service launches and grown sales by over $50M.",
    askAbout: ["Market development & launches", "Sales & go-to-market", "Partnerships, licensing & JVs"],
    availability: "Thu Oct 8, early afternoon · Tue Oct 13, 11 AM–2 PM on site at Demo Day.",
  }),
  seed({
    id: "rajiv-mehta",
    category: "office-hours",
    name: "Rajiv Mehta",
    title: "Speaker & author, Camaraderie",
    organization: "Camaraderie",
    expertise: "Sounding board on idea value",
    email: "rajivzume@gmail.com",
    photo: "https://cdn.magicpatterns.com/uploads/oxpBG4gTaVtHJLYfCheWzn/image.png",
    bio: "Rajiv Mehta studied aerospace engineering at Princeton and Stanford, was a NASA scientist, earned an MBA at Columbia, and led innovation at Apple and other Silicon Valley companies on very early product/market concepts. He founded an AI company whose technology eventually made its way into Siri, and wrote Camaraderie, on joyful, high-performing teams. Bring him your idea: who is it for, and why does it matter?",
    askAbout: ["Early product/market concepts", "Who is it for? Idea value", "Team culture & camaraderie"],
    availability: "Wed Oct 7, 1 PM onward · Fri Oct 9, 8:30 AM–noon. Virtual by default — email him to set a time.",
  }),
  seed({
    id: "dermot-mee",
    category: "office-hours",
    name: "Dermot Mee",
    title: "Chief of Staff, Fourier · DME HQ",
    organization: "Fourier · DME HQ",
    expertise: "Operations & scaling startups",
    email: "dermot@dmeehq.com",
    photo: "https://cdn.magicpatterns.com/uploads/ehM6MDNeuzDB8pGUYez6WU/image.png",
    linkedin: "https://www.linkedin.com/in/dermotmee/",
    bio: "Dermot Mee is a Bay Area operator who is passionate about supporting founders. He is Chief of Staff at Fourier, a Mountain View renewable-energy hardware startup, where he works alongside the CEO on strategy and day-to-day operations. He spent eight years at Singularity University, rising to Chief Operating Officer, and was briefly Chief Revenue Officer at Linqto. Earlier he spent more than a decade leading program management in biotech, at Medarex and Bristol-Myers Squibb, after starting his career as an analytical chemist.",
    askAbout: ["Operations & scaling a team", "Program & project management", "Business development & partnerships"],
    availability: "Office hours during build week (times TBA) · Demo Day guest.",
  }),
  seed({
    id: "kent-jenkins",
    category: "office-hours",
    name: "Kent Jenkins",
    title: "Angel Investor & Business Advisor",
    organization: "Angel Investor",
    expertise: "Angel investing",
    email: "kentjenkinslaw@gmail.com",
    photo: "https://cdn.magicpatterns.com/uploads/n6zS38RQbFx7kKSKVXy9kf/image.png",
    linkedin: "https://www.linkedin.com/in/kent-jenkins-07281b4/",
    bio: "Kent Jenkins is a San Francisco–based angel investor and business advisor who helps founders with business modeling and sales communications. He has advised Circuit Launch since 2016, helping hardware startups navigate the reality of building physical products, and also advises mechlabs, a build-it-to-learn-it mechatronics program, and the oral-care hardware startup Aurie. He previously mentored at the Hackers & Founders accelerator and ran Corporate Identity Systems for twenty years. He holds a JD from the University of San Francisco and a BA from Claremont McKenna College.",
    askAbout: ["Business models", "Sales & pitch communication", "Hardware startup advice"],
    availability: "Office hours / help on select days Oct 7–12.",
  }),

  // Investor & sponsor
  seed({
    id: "karen-traversi",
    category: "investor",
    name: "Karen Traversi",
    title: "Principal, SVH Capital",
    organization: "SVH Capital",
    expertise: "Pitch & business",
    email: "kt@svhcapital.com",
    photo: "https://cdn.magicpatterns.com/uploads/eZe3PkJ8TVzmgDz2jYyFTw/image.png",
    note: "Not at Demo Day",
    bio: "Karen Traversi is an investment banker, strategic advisor and former technology CEO with 30+ years helping founder-led and growth-stage companies scale and prepare for major strategic and financial events. As Founder and CEO of Traversi & Partners and a Principal with SVH Capital, she has built and scaled businesses, led acquisitions and integrations, and advised on capital raises, M&A and growth strategy.",
    askAbout: ["Capital raises & M&A", "Growth strategy & enterprise value", "Evaluating strategic options"],
    availability: "Mon Oct 12, 12–1 PM and 1:30–3:30 PM. Virtual — book via her Google Calendar link.",
  }),
  seed({
    id: "calvin-zhou",
    category: "judge",
    name: "Calvin Zhou",
    title: "Partner, Tess Ventures",
    organization: "Tess Ventures",
    expertise: "Demo Day judge",
    availability: "Demo Day judge: Tuesday Oct 13, from 2 PM PT, on site at Circuit Launch.",
    email: "",
    linkedin: "https://www.linkedin.com/in/calvinzhou86",
    photo: "https://cdn.magicpatterns.com/uploads/5gKVieNuiy4X1JNEUzgqLt/image.png",
    photoPosition: "50% 40%",
    bio: "Calvin Zhou is a Partner at Tess Ventures, a Silicon Valley venture fund investing in robotics, AI, space, nuclear, quantum, brain-computer interfaces, longevity and more. He also advises GaiaLabs on AI and robotics, was a strategy advisor to Leonardo.Ai through its acquisition by Canva, and co-founded the AI workforce platform Joinable Labs and the game studio SHRAPNEL. Earlier he founded and scaled the international e-commerce brand Rahmm, and began his career in finance in New York after studying at Columbia University.",
    askAbout: ["Raising venture capital", "Go-to-market & business strategy", "Scaling a brand"],
  }),
  seed({
    id: "kyle-valiton",
    category: "judge",
    name: "Kyle Valiton",
    title: "Co-founder, informal spaces · Owner, Build To Launch",
    organization: "informal spaces · Build To Launch",
    expertise: "Demo Day judge",
    availability: "Demo Day judge: Tuesday Oct 13, from 2 PM PT, on site at Circuit Launch.",
    email: "",
    linkedin: "https://www.linkedin.com/in/kvaliton/",
    photo: "https://cdn.magicpatterns.com/uploads/2xqYiCoo7TNFUCecb8VrZE/image.png",
    photoPosition: "50% 40%",
    bio: "Kyle Valiton is a mechanical engineer who turns product ideas into real, manufacturable hardware. Through his consultancy Build To Launch, he gives Bay Area startups on-site product design and manufacturing expertise, with a focus on medical devices. He ran Port Labs, an Oakland home for hardware companies in climate tech, IoT and robotics, and now co-founds informal spaces, a network of facilities connecting local builder communities. Earlier he was an R&D engineer on a bioprosthetic heart valve at Silara Medtech, and studied mechanical engineering at Cal Poly San Luis Obispo.",
    askAbout: ["Design for manufacturing", "Medical device development", "Bay Area supplier network"],
  }),
  seed({
    id: "jocelin-zheng",
    category: "sponsor",
    name: "Jocelin Zheng",
    title: "Business Development Manager, NexPCB | DisplayModule",
    organization: "NexPCB",
    expertise: "Prototype to production",
    email: "jocelin@nexpcb.com",
    photo: "https://cdn.magicpatterns.com/uploads/erqVodBjtbkWZbq99ML6g6/image.png",
    bio: "NexPCB joins as a sponsor mentor, drawing on its past hackathon experience. Its prize, Complimentary NexPCB Innovation Consulting, lets the winning team work directly with NexPCB's project team to map the path from prototype to real product: a feasibility and architecture review, a budget from prototype through mass production, a delivery plan with milestones, and certification and regulatory research.",
    askAbout: ["Prototype-to-production budgeting", "Feasibility & architecture review", "Certification & regulatory"],
    availability: "Mentor sessions to be announced — email Jocelin with questions.",
  }),

  // Circuit Launch hosts
  seed({
    id: "shermie-miller-zhang",
    category: "host",
    name: "Shermie Miller-Zhang",
    title: "COO & Co-founder, Circuit Launch and MechLabs",
    organization: "Circuit Launch",
    expertise: "Program & mentor connections",
    email: "shermie@circuitlaunch.com",
    photo: "https://cdn.magicpatterns.com/uploads/2bY3YigjhHzTAMWXbHoReS/image.png",
    photoPosition: "30% 30%",
    bio: "On site all week, Oct 7–13. Find her in the building or email to set up office hours.",
    askAbout: ["Program questions", "Connections to mentors"],
    availability: "On site all week, Oct 7–13.",
  }),
  seed({
    id: "alex-dantas",
    category: "host",
    name: "Alex Dantas",
    title: "CEO, Circuit Launch",
    organization: "Circuit Launch",
    expertise: "Venture building",
    email: "alex@circuitlaunch.com",
    photo: "https://cdn.magicpatterns.com/uploads/dJ61FmpHZ12ev7gAFHLtDu/image.png",
    bio: "On site all week, Oct 7–13. Find him in the building or email to set up office hours.",
    askAbout: ["Venture building", "The Circuit Launch community"],
    availability: "On site all week, Oct 7–13.",
  }),
  seed({
    id: "nathan-cooke",
    category: "host",
    name: "Nathan Cooke",
    title: "Shop Manager, Circuit Launch",
    organization: "Circuit Launch",
    expertise: "Shop & tools",
    email: "nathan@circuitlaunch.com",
    photo: "https://cdn.magicpatterns.com/uploads/vzrztzMwuPm1DXZYsthw1g/image.png",
    photoPosition: "62% 15%",
    bio: "On site all week, Oct 7–13. Find him in the building or email to set up office hours.",
    askAbout: ["The shop, tools and equipment"],
    availability: "On site all week, Oct 7–13.",
  }),

  // Link School of Business hosts
  seed({
    id: "victor-sanacato",
    category: "link-host",
    name: "Victor Sanacato",
    title: "Partner & COO, Link School of Business",
    organization: "Link School of Business",
    expertise: "Partner & COO",
    email: "",
    photo: "https://cdn.magicpatterns.com/uploads/8wPtYW98qrwDXnNJhK4RvU/image.png",
    linkedin: "https://www.linkedin.com/in/victorsanacato",
    bio: "Victor Sanacato is Partner and COO of Link School of Business in São Paulo, where he leads technology and operations across its São Paulo and Miami campuses and its lodges in Silicon Valley, Boston and Madrid. Before that, he built Link's International Affairs from the ground up, sending 400+ students abroad each year with partnerships across four continents, work Babson College recognised as the world's most innovative entrepreneurial education project. A serial entrepreneur, he previously ran the venture studio bashō studio, led innovation at Innoway and Grupo Educacional UNIS, and founded businesses in sustainable engineering and events.",
    askAbout: ["Entrepreneurship & venture building", "Innovation strategy", "Link School of Business"],
    availability: "With the program all week, Oct 7–13.",
  }),
  seed({
    id: "luiza-arntsen",
    category: "link-host",
    name: "Luiza Arntsen",
    title: "Head of International Affairs, Link School of Business",
    organization: "Link School of Business",
    expertise: "Head of International Affairs",
    email: "",
    photo: "https://cdn.magicpatterns.com/uploads/uHnHvcVzi7vVJvpt8ZSKiw/image.png",
    linkedin: "https://www.linkedin.com/in/luiza-arntsen/",
    bio: "Luiza Arntsen is Head of International Affairs at Link School of Business in São Paulo, where she runs the school's international programs end to end, from operations to relationships with global partners. She designs immersive journeys that give students real access to leaders and innovation ecosystems around the world. She previously managed organisational culture and training projects at W.G. Weird Garage, and produced corporate events and Design Thinking workshops at weme. She studied Public Relations and Advertising at PUC-Campinas.",
    askAbout: ["International programs", "Program questions & logistics", "Link School of Business"],
    availability: "With the program all week, Oct 7–13.",
  }),
];

/** Ids of the placeholder mentors that shipped before the real lineup — dropped on load. */
export const retiredMentorIds = ["m1", "m2", "m3", "m4"];
